import { useCallback, useEffect, useReducer } from "react";

import { getUser } from "@/components/session/JwtTokenManager";
import { ShareUser } from "@/components/ui/sharing/types";
import {
  FullRoleplaySession,
  ScenarioRoleplaySession,
} from "@/types/roleplays";

// State types
export interface SessionShareState {
  // Selection state
  selectedUsers: ShareUser[];

  // Notification state
  isNotified: boolean;
  message: string;

  // Session data
  sharedMembers: FullRoleplaySession["sharedMembers"];
  programs: FullRoleplaySession["programs"];
  owner: FullRoleplaySession["user"] | null;
}

// Action types
type SessionShareAction =
  | { type: "SET_SELECTED_USERS"; payload: ShareUser[] }
  | {
      type: "SET_NOTIFICATION";
      payload: { isNotified: boolean; message: string };
    }
  | {
      type: "UPDATE_SESSION_DATA";
      payload: {
        sharedMembers: SessionShareState["sharedMembers"];
        programs: SessionShareState["programs"];
        owner: SessionShareState["owner"];
      };
    }
  | { type: "RESET_ALL" };

// Reducer
function sessionShareReducer(
  state: SessionShareState,
  action: SessionShareAction,
): SessionShareState {
  switch (action.type) {
    case "SET_SELECTED_USERS":
      return { ...state, selectedUsers: action.payload };

    case "SET_NOTIFICATION":
      return {
        ...state,
        isNotified: action.payload.isNotified,
        message: action.payload.message,
      };

    case "UPDATE_SESSION_DATA":
      return {
        ...state,
        sharedMembers: action.payload.sharedMembers,
        programs: action.payload.programs,
        owner: action.payload.owner,
      };

    case "RESET_ALL":
      return {
        ...state,
        selectedUsers: [],
        isNotified: true,
        message: "",
      };

    default:
      return state;
  }
}

type RoleplaySession = (FullRoleplaySession | ScenarioRoleplaySession) & {
  programs?: FullRoleplaySession["programs"];
  sharedMembers?: FullRoleplaySession["sharedMembers"];
  user?: FullRoleplaySession["user"];
};

interface UseSessionShareStateProps {
  session: RoleplaySession;
}

export function useSessionShareState({ session }: UseSessionShareStateProps) {
  const currentUser = getUser();

  const initialState: SessionShareState = {
    selectedUsers: [],
    isNotified: true,
    message: "",
    sharedMembers: session.sharedMembers || [],
    programs: session.programs || [],
    owner: session.user || null,
  };

  const [state, dispatch] = useReducer(sessionShareReducer, initialState);

  // Update state when session data changes
  useEffect(() => {
    dispatch({
      type: "UPDATE_SESSION_DATA",
      payload: {
        sharedMembers: session.sharedMembers || [],
        programs: session.programs || [],
        owner: session.user || null,
      },
    });
  }, [session.sharedMembers, session.programs, session.user]);

  // Helper functions
  const setSelectedUsers = useCallback((users: ShareUser[]) => {
    dispatch({ type: "SET_SELECTED_USERS", payload: users });
  }, []);

  const setNotification = useCallback(
    (isNotified: boolean, message: string) => {
      dispatch({ type: "SET_NOTIFICATION", payload: { isNotified, message } });
    },
    [],
  );

  const resetAll = useCallback(() => {
    dispatch({ type: "RESET_ALL" });
  }, []);

  return {
    state,
    currentUser,
    setSelectedUsers,
    setNotification,
    resetAll,
  };
}
